import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    computed,
    contentChildren,
    inject,
    input,
    signal,
} from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { QpxCmdkItem } from './item';

@Component({
    selector: 'qpx-cmdk-list',
    template: '<ng-content />',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'qpx-cmdk-list',
        role: 'listbox',
        '[id]': 'listId',
        '[attr.aria-label]': 'ariaLabel()',
        '[class.active]': 'active()',
    },
})
export class QpxCmdkList implements AfterViewInit, OnDestroy {
    //#region Dependency injection
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly cdr = inject(ChangeDetectorRef);
    //#endregion

    //#region Inputs
    public readonly ariaLabel = input<string | undefined>();
    //#endregion

    //#region Queries
    private readonly items = contentChildren(QpxCmdkItem, {
        descendants: true,
    });
    //#endregion

    //#region State
    public readonly listId = `cmdk-list-${uuidv4()}`;
    protected readonly active = signal(false);
    private animationFrame: number | undefined;
    private observer: ResizeObserver | undefined;
    //#endregion

    //#region Derived state
    public readonly filteredItems = computed(() =>
        this.items().filter((item) => item.filtered())
    );
    public readonly filtered = computed(() => this.filteredItems().length > 0);
    //#endregion

    //#region Lifecycle hooks
    public ngAfterViewInit(): void {
        const host = this.elementRef.nativeElement;
        this.observer = new ResizeObserver(() => {
            this.animationFrame = requestAnimationFrame(() => {
                const height = host.getBoundingClientRect().height;
                host.style.setProperty(
                    '--cmdk-list-height',
                    `${height.toFixed(1)}px`
                );
                this.cdr.markForCheck();
            });
        });
        this.observer.observe(host);
    }

    public ngOnDestroy(): void {
        if (this.animationFrame !== undefined) {
            cancelAnimationFrame(this.animationFrame);
        }

        this.observer?.disconnect();
    }
    //#endregion

    //#region Public methods
    public setActive(value: boolean): void {
        this.active.set(value);
        this.cdr.markForCheck();
    }
    //#endregion
}
